// ─── src/MentorsView.tsx ──────────────────────────────────────────────────────
import { useState, useCallback, useMemo } from "react";
import { Download } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMentors, mentorKeys }         from "./hooks/useMentors";
import { useMentorStats, statsKeys }      from "./hooks/useStats";
import { getMentors }                     from "./api/mentors";
import { MentorStats }     from "./components/dashboard/MentorStats";
import { MentorFilterBar, DEFAULT_MENTOR_FILTERS } from "./components/dashboard/MentorFilterBar";
import type { MentorFilters } from "./components/dashboard/MentorFilterBar";
import { MentorsTable }    from "./components/dashboard/MentorsTable";
import { Pagination }      from "./components/dashboard/Pagination";
import {
  MentorTopicsChart,
  MentorCitiesChart,
  MentorFrequencyChart,
  MentorModeChart,
} from "./components/dashboard/MentorCharts";
import { TableSkeleton }   from "./components/ui/Skeleton";
import { EmptyState }      from "./components/ui/EmptyState";
import type { Mentor, MentorParams } from "./types/mentor";
import type { MentorStatsResponse }  from "./types/stats";
import { toChartEntries }            from "./types/stats";

const LIMIT = 10;

// ── CSV export ─────────────────────────────────────────────────────────────────
function exportMentorsToCsv(mentors: Mentor[]) {
  const escape = (v: unknown) => {
    const s = v == null ? "" : Array.isArray(v) ? v.join("; ") : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const COLS: { label: string; get: (m: Mentor) => unknown }[] = [
    { label: "Name",                    get: (m) => m.name },
    { label: "Email",                   get: (m) => m.email },
    { label: "Phone",                   get: (m) => m.phone },
    { label: "Location",                get: (m) => m.location },
    { label: "LinkedIn",                get: (m) => m.linkedin },
    { label: "Company",                 get: (m) => m.company },
    { label: "Current Role",            get: (m) => m.currentRole },
    { label: "Professional Background", get: (m) => m.professionalBackground },
    { label: "Topics",                  get: (m) => m.topics },
    { label: "Other Topic",             get: (m) => m.otherTopic },
    { label: "Frequency",               get: (m) => m.frequency },
    { label: "Mode",                    get: (m) => m.mode },
    { label: "Cities",                  get: (m) => m.cities },
    { label: "Additional Notes",        get: (m) => m.additionalNotes },
    { label: "W1 Thu Morning",          get: (m) => m.weekendSchedule?.weekend1?.thursday.morning   ? "Yes" : "No" },
    { label: "W1 Thu Afternoon",        get: (m) => m.weekendSchedule?.weekend1?.thursday.afternoon ? "Yes" : "No" },
    { label: "W1 Thu Evening",          get: (m) => m.weekendSchedule?.weekend1?.thursday.evening   ? "Yes" : "No" },
    { label: "W1 Fri Morning",          get: (m) => m.weekendSchedule?.weekend1?.friday.morning     ? "Yes" : "No" },
    { label: "W1 Fri Afternoon",        get: (m) => m.weekendSchedule?.weekend1?.friday.afternoon   ? "Yes" : "No" },
    { label: "W1 Fri Evening",          get: (m) => m.weekendSchedule?.weekend1?.friday.evening     ? "Yes" : "No" },
    { label: "W1 Sat Morning",          get: (m) => m.weekendSchedule?.weekend1?.saturday.morning   ? "Yes" : "No" },
    { label: "W1 Sat Afternoon",        get: (m) => m.weekendSchedule?.weekend1?.saturday.afternoon ? "Yes" : "No" },
    { label: "W1 Sat Evening",          get: (m) => m.weekendSchedule?.weekend1?.saturday.evening   ? "Yes" : "No" },
    { label: "W2 Thu Morning",          get: (m) => m.weekendSchedule?.weekend2?.thursday.morning   ? "Yes" : "No" },
    { label: "W2 Thu Afternoon",        get: (m) => m.weekendSchedule?.weekend2?.thursday.afternoon ? "Yes" : "No" },
    { label: "W2 Thu Evening",          get: (m) => m.weekendSchedule?.weekend2?.thursday.evening   ? "Yes" : "No" },
    { label: "W2 Fri Morning",          get: (m) => m.weekendSchedule?.weekend2?.friday.morning     ? "Yes" : "No" },
    { label: "W2 Fri Afternoon",        get: (m) => m.weekendSchedule?.weekend2?.friday.afternoon   ? "Yes" : "No" },
    { label: "W2 Fri Evening",          get: (m) => m.weekendSchedule?.weekend2?.friday.evening     ? "Yes" : "No" },
    { label: "W2 Sat Morning",          get: (m) => m.weekendSchedule?.weekend2?.saturday.morning   ? "Yes" : "No" },
    { label: "W2 Sat Afternoon",        get: (m) => m.weekendSchedule?.weekend2?.saturday.afternoon ? "Yes" : "No" },
    { label: "W2 Sat Evening",          get: (m) => m.weekendSchedule?.weekend2?.saturday.evening   ? "Yes" : "No" },
    { label: "Registered At",           get: (m) => m.createdAt },
  ];
  const header = COLS.map((c) => c.label).join(",");
  const body   = mentors.map((m) => COLS.map((c) => escape(c.get(m))).join(","));
  const csv    = [header, ...body].join("\n");
  const blob   = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement("a");
  a.href       = url;
  a.download   = `mentors_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildMentorCharts(stats: MentorStatsResponse) {
  const total      = stats.total;
  const topicTotal = stats.byTopic.reduce((a, e) => a + e.count, 0);
  const cityTotal  = stats.byCity.reduce((a, e) => a + e.count, 0);
  return {
    topics:    toChartEntries(stats.byTopic,     topicTotal),
    cities:    toChartEntries(stats.byCity,      cityTotal),
    frequency: toChartEntries(stats.byFrequency, total),
    mode:      toChartEntries(stats.byMode,      total),
  };
}

export default function MentorsView() {
  const qc = useQueryClient();

  const [filters,    setFilters]    = useState<MentorFilters>(DEFAULT_MENTOR_FILTERS);
  const [visualPage, setVisualPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // ── Stats query: aggregated counts only (one lightweight request)
  const { data: stats, isLoading: statsLoading } = useMentorStats();

  const chartData    = useMemo(() => (stats ? buildMentorCharts(stats) : null), [stats]);
  const filterCities = useMemo(() => (stats?.byCity ?? []).map((e) => e.name).sort(), [stats]);
  const filterTopics = useMemo(() => (stats?.byTopic ?? []).map((e) => e.name).sort(), [stats]);

  // ── Table query: paginated + server-filtered
  const tableParams = useMemo((): MentorParams => ({
    page:  visualPage,
    limit: LIMIT,
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.frequency !== "all" ? { frequency: filters.frequency } : {}),
    ...(filters.mode !== "all" ? { mode: filters.mode } : {}),
    ...(filters.city !== "all" ? { city: filters.city } : {}),
    ...(filters.topic !== "all" ? { topic: filters.topic } : {}),
  }), [filters, visualPage]);

  const { data: tableData, isLoading, isFetching, isError, error } = useMentors(tableParams);

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  // ── Handlers
  const handleFiltersChange = useCallback((f: MentorFilters) => {
    setFilters(f);
    setVisualPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(DEFAULT_MENTOR_FILTERS);
    setVisualPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: mentorKeys.all });
    qc.invalidateQueries({ queryKey: statsKeys.mentors() });
  }, [qc]);

  // ── CSV export: on-demand fetch (uses cache if fresh, otherwise fetches once)
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await qc.fetchQuery({
        queryKey: mentorKeys.list({ page: 1, limit: 500 }),
        queryFn:  () => getMentors({ page: 1, limit: 500 }),
        staleTime: 30_000,
      });
      exportMentorsToCsv(result.data);
    } finally {
      setIsExporting(false);
    }
  }, [qc]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && <MentorStats stats={stats} isLoading={statsLoading} />}
      {statsLoading && !stats && (
        <MentorStats
          stats={{ total: 0, online: 0, onsite: 0, onDemand: 0, byTopic: [], byCity: [], byFrequency: [], byMode: [] }}
          isLoading={true}
        />
      )}

      {/* Charts — 2×2 grid */}
      {!statsLoading && chartData && (
        <>
          <div className="flex flex-col xl:flex-row gap-6">
            <MentorTopicsChart data={chartData.topics} />
            <MentorCitiesChart data={chartData.cities} />
          </div>
          <div className="flex flex-col xl:flex-row gap-6">
            <MentorFrequencyChart data={chartData.frequency} total={stats?.total} />
            <MentorModeChart      data={chartData.mode} />
          </div>
        </>
      )}

      {/* Filters + Download */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <MentorFilterBar
            filters={filters}
            cities={filterCities}
            topics={filterTopics}
            onChange={handleFiltersChange}
            onReset={handleReset}
          />
        </div>
        <button
          onClick={handleExport}
          disabled={isExporting || statsLoading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl
                     bg-brand-card border border-brand-border
                     text-sm font-medium text-brand-muted
                     hover:text-white hover:border-brand-orange/50
                     transition-colors shrink-0 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {isExporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ErrorState message={(error as Error).message} onRetry={handleRefresh} />
        ) : pageRows.length === 0 ? (
          <EmptyState />
        ) : (
          <MentorsTable mentors={pageRows} />
        )}
      </div>

      {/* Pagination */}
      {!isLoading && tableMeta.totalPages > 1 && (
        <Pagination
          page={tableMeta.page}
          totalPages={tableMeta.totalPages}
          total={tableMeta.total}
          limit={LIMIT}
          onPageChange={setVisualPage}
          isFetching={isFetching}
        />
      )}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-6">
      <p className="text-red-400 font-medium">{message}</p>
      <button onClick={onRetry} className="btn-primary text-sm">Try Again</button>
    </div>
  );
}
